import React, {useEffect} from 'react'
import {
  SelectPureWithInputContainer,
  COMPANY_CIN_CODE,
  InputComponent,
  COMPANY_INDUSTRY,
  COMPANY_LEGAL_FORM,
  RawFormInputContext,
  useInputIsVisible,
  InputTooltipInfo,
} from '@modifi/plugin-onboarding'
import {
  Col,
  Row,
  withTooltip,
  AttentionIcon,
  CheckboxPure,
  Small,
  withResponsiveHelper,
  InlineLoader,
  Strong,
} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {useInputValue} from '@modifi/plugin-forms'
import {PublicRegisteredCompaniesRetrievalStatus} from '@modifi/enums'
import {warning, darkGray} from '@modifi/colors'

import {AddressType, formatOptionsLabel, mapCompanyToOptions} from '../utils'
import {useDataPrefillLoader} from '../../../hooks'
import {
  COMPANY_SELECT_NONE_TEXT,
  COMPANY_FOUNDATION_MONTH,
  COMPANY_FOUNDATION_YEAR,
  COMPANY_NAME_SELECT,
} from '../../constants'
import {OptionLabelCompanyName} from '../../../data-source-wizard/styles'
import {
  CompanyNameNoOptionInput,
  CompanyNameNoOptionLabel,
  CompanyNameNoOptionWrapper,
  FormContentWrapper,
  WarningCol,
} from '../../styles'
import {FormRow} from '../../../../../ui/form'
import {FormInputComponentCol} from '../../../../../plugins/onboarding'
import {StyledAddIcon} from './styles'

const AttentionIconWithTooltip = withTooltip(AttentionIcon)

const yearOptions: {label: string; value: number}[] = []
for (let year = new Date().getFullYear(); year >= 1800; year -= 1) {
  yearOptions.push({label: `${year}`, value: year})
}

export type CompanyIntroductionFormContentProps = {
  isLoading?: boolean
  externalCompanyId: string
  retrievalStatus?: string
  countryCode: string
  possibleCompanyMatches?: {
    address?: {
      companyName?: string
      streetName?: string
      streetNumber?: string
      state?: string
      zipCode?: string
      city?: string
      countryCode?: string
    }
    providerCompanyId?: string
  }[]
  hasCompanyCinCode?: boolean
  legalFormOptions?: string[]
  fetchLegalForms: (countryCode: string) => void
  industryOptions?: {
    id?: number
    code?: string
    description?: string
  }[]
  fetchIndustries: () => void
  fetchCompanyRegistration?: (externalCompanyId: string) => void
  consent: boolean
  handleConsentChange: () => void
  hasFoundationDateInputField?: boolean
  isSmOrSmaller?: boolean
  isCompanyLocked?: boolean
  companyAddress?: (AddressType & {companyName?: string | null}) | null
}

const CompanyIntroductionFormContent: React.FC<CompanyIntroductionFormContentProps> = ({
  isLoading = true,
  externalCompanyId,
  retrievalStatus = PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION,
  countryCode,
  possibleCompanyMatches = [],
  hasCompanyCinCode = false,
  legalFormOptions = [],
  fetchLegalForms,
  industryOptions = [],
  fetchIndustries,
  fetchCompanyRegistration,
  consent,
  handleConsentChange,
  hasFoundationDateInputField = false,
  isSmOrSmaller = false,
  isCompanyLocked = false,
  companyAddress,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  useDataPrefillLoader()

  useEffect(() => {
    fetchLegalForms(countryCode)
  }, [countryCode, fetchLegalForms])

  useEffect(() => {
    fetchCompanyRegistration && fetchCompanyRegistration(externalCompanyId)
  }, [fetchCompanyRegistration, externalCompanyId])

  useEffect(() => {
    fetchIndustries()
  }, [fetchIndustries])

  const {value: companyNameSelectValue} = useInputValue({
    name: COMPANY_NAME_SELECT,
  })
  const {value: companyLegalFormValue} = useInputValue({
    name: COMPANY_LEGAL_FORM,
  })
  const {value: companyFormedMonthValue} = useInputValue({
    name: COMPANY_FOUNDATION_MONTH,
  })
  const {value: companyFormedYearValue} = useInputValue({
    name: COMPANY_FOUNDATION_YEAR,
  })

  const cinCodeInputIsVisible = useInputIsVisible(COMPANY_CIN_CODE)

  if (isLoading) {
    return (
      <Row justifyContent="center">
        <Col auto>
          <InlineLoader />
        </Col>
      </Row>
    )
  }

  // @ts-expect-error @todo_pre_underwriting to fix typing for mapCompanyToOptions
  const companyOptions = mapCompanyToOptions(possibleCompanyMatches as any)

  companyOptions.push({
    label: (
      <Row alignItems="center">
        <StyledAddIcon color={darkGray} />{' '}
        <OptionLabelCompanyName>
          {t(`company-name-select.${COMPANY_SELECT_NONE_TEXT}.option`)}
        </OptionLabelCompanyName>
      </Row>
    ),
    value: COMPANY_SELECT_NONE_TEXT,
  })

  const monthOptions = []
  for (let month = 0; month < 12; month += 1) {
    const date = new Date(2018, month, 2)
    const monthName = date.toLocaleString('en-GB', {month: 'long', timeZone: 'UTC'})
    monthOptions.push({
      label: t(`company-formed-month.${monthName.toLowerCase()}.option`),
      value: month,
    })
  }

  const showCompanySelectionIndicator =
    companyNameSelectValue !== COMPANY_SELECT_NONE_TEXT &&
    retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION

  let showCompanySelectDropdown = true
  if (
    retrievalStatus === PublicRegisteredCompaniesRetrievalStatus.COMPANY_NOT_FOUND ||
    possibleCompanyMatches?.length === 0
  ) {
    showCompanySelectDropdown = false
  }

  return (
    <FormContentWrapper>
      {showCompanySelectDropdown ? (
        <Row alignItems="center">
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_NAME_SELECT}
            inputComponent={SelectPureWithInputContainer}
            options={companyOptions}
            inputProps={{
              label: t('intro-page.company-name-select.label'),
              placeholder: t('intro-page.company-name-select.placeholder'),
              required: true,
              disabled:
                retrievalStatus !== PublicRegisteredCompaniesRetrievalStatus.PENDING_RESOLUTION ||
                isCompanyLocked,
              labelAddition: isSmOrSmaller && showCompanySelectionIndicator && (
                <AttentionIcon color={warning} />
              ),
            }}
            isAlwaysVisible
            colProps={{md: 10, lg: 8}}
          />
          {showCompanySelectionIndicator &&
            (isSmOrSmaller ? (
              <WarningCol>
                {t(
                  'data-pre-selection.company-selection-confirmation-dialog.confirmation-description'
                )}
              </WarningCol>
            ) : (
              <Col col="auto">
                <AttentionIconWithTooltip
                  height={40}
                  width={40}
                  color={warning}
                  tooltipDefaultShown
                  tooltipDisabled
                  tooltipPlacement="right"
                  tooltipContent={t(
                    'data-pre-selection.company-selection-confirmation-dialog.confirmation-description'
                  )}
                />
              </Col>
            ))}
        </Row>
      ) : (
        <CompanyNameNoOptionWrapper data-ident="company-name-select-placeholder">
          <CompanyNameNoOptionLabel mb={8}>
            <Strong>{t('intro-page.company-name-select.label')}</Strong>
          </CompanyNameNoOptionLabel>
          <CompanyNameNoOptionInput md={10} lg={8}>
            <>
              {companyAddress &&
                formatOptionsLabel({label: companyAddress.companyName, address: companyAddress})}
            </>
          </CompanyNameNoOptionInput>
        </CompanyNameNoOptionWrapper>
      )}
      {(companyNameSelectValue === COMPANY_SELECT_NONE_TEXT ||
        !companyNameSelectValue ||
        hasCompanyCinCode) && (
        <>
          <Row>
            <FormInputComponentCol
              t={t}
              inputName={COMPANY_CIN_CODE}
              inputComponent={InputComponent}
              inputProps={{
                placeholder: t('intro-page.company-cin-code.placeholder'),
                disabled: hasCompanyCinCode,
              }}
              colProps={{md: 10, lg: 8}}
            />
          </Row>
          {!hasCompanyCinCode && cinCodeInputIsVisible && (
            <Row>
              <Col md={10} lg={8} mb={16} mt={-8}>
                <RawFormInputContext.Consumer>
                  {({globalReadonly}) => (
                    <CheckboxPure
                      name="consent"
                      label={<Small>{t('data-for-prefill-page.consent.text')}</Small>}
                      value
                      checked={consent}
                      onChange={handleConsentChange}
                      disabled={globalReadonly}
                    />
                  )}
                </RawFormInputContext.Consumer>
              </Col>
            </Row>
          )}
        </>
      )}
      {!!legalFormOptions?.length && (
        <Row>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_LEGAL_FORM}
            inputComponent={SelectPureWithInputContainer}
            options={legalFormOptions.map(legalForm => ({
              label: legalForm,
              value: legalForm,
            }))}
            inputProps={{
              label: t('intro-page.company-legal-form-select.label'),
              required: true,
            }}
            colProps={{md: 10, lg: 8}}
          />
        </Row>
      )}
      {companyLegalFormValue && (
        <FormRow>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_FOUNDATION_MONTH}
            inputComponent={SelectPureWithInputContainer}
            options={monthOptions}
            inputProps={{
              label: t('intro-page.company-formed-date.label'),
              placeholder: t('intro-page.company-formed-month.placeholder'),
              required: true,
            }}
            colProps={{md: 4}}
            isAlwaysVisible={hasFoundationDateInputField}
          />
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_FOUNDATION_YEAR}
            inputComponent={SelectPureWithInputContainer}
            options={yearOptions}
            inputProps={{
              placeholder: t('intro-page.company-formed-year.placeholder'),
              required: true,
            }}
            colProps={{md: 4}}
            isAlwaysVisible={hasFoundationDateInputField}
          />
        </FormRow>
      )}
      {companyFormedMonthValue != null && companyFormedYearValue && !!industryOptions?.length && (
        <Row mb={16}>
          <FormInputComponentCol
            t={t}
            inputName={COMPANY_INDUSTRY}
            inputComponent={SelectPureWithInputContainer}
            options={industryOptions.map(industry => ({
              label: t(`industry.option.${industry.code}`),
              value: industry?.code || '',
            }))}
            inputProps={{
              label: t('intro-page.company-industry.label'),
              required: true,
              labelAddition: <InputTooltipInfo inputName={COMPANY_INDUSTRY} t={t} />,
            }}
            colProps={{md: 10, lg: 8}}
          />
        </Row>
      )}
    </FormContentWrapper>
  )
}

export default withResponsiveHelper(React.memo(CompanyIntroductionFormContent))
